import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogImage?: string;
  ogImageAlt?: string;
  noindex?: boolean;
}

export function SEO({
  title = 'מספרת כלבים בזכרון־יעקב — ShowGroom',
  description = 'ShowGroom — מספרת כלבים וחתולים בזכרון־יעקב. אמבטיה, תספורות, גזירת ציפורניים וטיפולים היפואלרגניים. קבעו תור: 077-2200052',
  canonical = 'https://showgroom.co.il/',
  ogImage = 'https://showgroom.co.il/images/showgroom-og.png',
  ogImageAlt = 'ShowGroom - מספרת כלבים וחתולים מקצועית בזכרון־יעקב',
  noindex = false,
}: SEOProps) {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Update or create meta tags
    const updateMeta = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.content = content;
    };

    // Update link tags
    const updateLink = (rel: string, href: string) => {
      let element = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
      
      if (!element) {
        element = document.createElement('link');
        element.rel = rel;
        document.head.appendChild(element);
      }
      element.href = href;
    };

    // Basic meta tags
    updateMeta('description', description);
    updateMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    
    // Open Graph tags
    updateMeta('og:type', 'website', true);
    updateMeta('og:title', title, true);
    updateMeta('og:description', description, true);
    updateMeta('og:url', canonical, true);
    updateMeta('og:site_name', 'ShowGroom', true);
    updateMeta('og:image', ogImage, true);
    updateMeta('og:image:alt', ogImageAlt, true);
    updateMeta('og:locale', 'he_IL', true);

    // Twitter Card tags
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', title);
    updateMeta('twitter:description', description);
    updateMeta('twitter:image', ogImage);
    updateMeta('twitter:image:alt', ogImageAlt);

    // Canonical link
    updateLink('canonical', canonical);

    // Language and direction
    document.documentElement.lang = 'he';
    document.documentElement.dir = 'rtl';

  }, [title, description, canonical, ogImage, ogImageAlt, noindex]);

  return null;
}

// Structured Data Components
export function LocalBusinessSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'PetGroomer',
    'name': 'ShowGroom',
    'alternateName': 'ShowGroom — מספרת כלבים בזכרון־יעקב',
    'image': 'https://showgroom.co.il/images/showgroom-og.png',
    'telephone': '+972772200052',
    'email': 'info@showgroom.co.il',
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': 'רח\' המייסדים 51',
      'addressLocality': 'זכרון יעקב',
      'addressRegion': 'חיפה',
      'postalCode': '3095212',
      'addressCountry': 'IL'
    },
    'url': 'https://showgroom.co.il',
    'priceRange': '₪₪',
    'description': 'מספרת כלבים וחתולים בזכרון־יעקב — אמבטיה, תספורות, גזירת ציפורניים וטיפוח היפואלרגני. טיפוח מקצועי ועדין לכלב או חתול שלכם.',
    'sameAs': [
      'https://www.facebook.com/showgroom.2025',
      'https://www.instagram.com/showgroom_25'
    ],
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': 32.57354617615167,
      'longitude': 34.95306734589387
    },
    'openingHoursSpecification': [
      {
        '@type': 'OpeningHoursSpecification',
        'dayOfWeek': ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'],
        'opens': '09:00',
        'closes': '17:00'
      },
      {
        '@type': 'OpeningHoursSpecification',
        'dayOfWeek': 'Friday',
        'opens': '09:00',
        'closes': '14:00'
      }
    ],
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'שירותי טיפוח',
      'itemListElement': [
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'תספורת לכלב',
            'description': 'תספורת מקצועית מותאמת גזע וסגנון'
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'אמבטיה לכלב',
            'description': 'אמבטיה עם שמפו עדין ומרכך, ייבוש מקצועי'
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'גזירת ציפורניים',
            'description': 'גזירת ציפורניים בטוחה ומדויקת'
          }
        },
        {
          '@type': 'Offer',
          'itemOffered': {
            '@type': 'Service',
            'name': 'טיפוח חתולים',
            'description': 'טיפול עדין ונטול-לחץ בחתולים'
          }
        }
      ]
    }
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = 'schema-local-business';
    
    // Remove existing if present
    const existing = document.getElementById('schema-local-business');
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  return null;
}

export function OrganizationSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': 'ShowGroom',
    'url': 'https://showgroom.co.il',
    'logo': 'https://showgroom.co.il/images/showgroom-og.png',
    'contactPoint': {
      '@type': 'ContactPoint',
      'telephone': '+972772200052',
      'contactType': 'customer service',
      'areaServed': 'IL',
      'availableLanguage': ['Hebrew', 'English', 'Russian']
    },
    'sameAs': [
      'https://www.facebook.com/showgroom.2025',
      'https://www.instagram.com/showgroom_25'
    ]
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = 'schema-organization';
    
    const existing = document.getElementById('schema-organization');
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  return null;
}

export function FAQSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': [
      {
        '@type': 'Question',
        'name': 'האם אתם מטפחים גם חתולים?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'כן! ShowGroom מספקת שירותי טיפוח מקצועיים לכלבים וגם לחתולים, כולל אמבטיה, תספורת וגזירת ציפורניים.'
        }
      },
      {
        '@type': 'Question',
        'name': 'כמה עולה תספורת לכלב?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'המחירים משתנים בהתאם לגודל הכלב וסוג הטיפול. צרו קשר בטלפון 077-2200052 לקבלת הצעת מחיר מדויקת.'
        }
      },
      {
        '@type': 'Question',
        'name': 'האם אתם גוזרים ציפורניים?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'כן, גזירת ציפורניים היא חלק משירותי הטיפוח שלנו. ניתן לקבל את השירות בנפרד או כחלק מחבילת טיפוח מלאה.'
        }
      },
      {
        '@type': 'Question',
        'name': 'כל כמה זמן צריך לספר את הכלב?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'תלוי בגזע הכלב וסוג הפרווה. בדרך כלל מומלץ כל 4-8 שבועות. כלבים עם פרווה ארוכה כמו יורקשייר או שיצו צריכים טיפול תכוף יותר.'
        }
      },
      {
        '@type': 'Question',
        'name': 'האם יש לכם חומרים היפואלרגניים?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'כן! אנחנו מציעים טיפולים היפואלרגניים לכלבים וחתולים עם עור רגיש, עם שמפו וחומרים מיוחדים.'
        }
      },
      {
        '@type': 'Question',
        'name': 'כמה זמן לוקח טיפוח מלא?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'טיפוח מלא כולל אמבטיה ותספורת לוקח בדרך כלל בין 90 ל-120 דקות, תלוי בגודל הכלב ומצב הפרווה.'
        }
      },
      {
        '@type': 'Question',
        'name': 'מה מדיניות הביטולים?',
        'acceptedAnswer': {
          '@type': 'Answer',
          'text': 'אנא הודיעו על ביטול או שינוי לפחות 24 שעות מראש. ביטול באיחור עלול לגרור חיוב.'
        }
      }
    ]
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = 'schema-faq';
    
    const existing = document.getElementById('schema-faq');
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  return null;
}

export function BreadcrumbSchema() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'בית',
        'item': 'https://showgroom.co.il/'
      },
      {
        '@type': 'ListItem',
        'position': 2,
        'name': 'שירותים',
        'item': 'https://showgroom.co.il/#services'
      },
      {
        '@type': 'ListItem',
        'position': 3,
        'name': 'קביעת תור',
        'item': 'https://showgroom.co.il/#booking'
      }
    ]
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = 'schema-breadcrumb';
    
    const existing = document.getElementById('schema-breadcrumb');
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  return null;
}
